import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {useMemo} from 'react';

import * as Filters from '../filters';
import type {Filter} from '../filterTypes';
import {runIdsToNames, runIdToProject} from '../selectionmanager';

const RUN_NAME = Filters.Key('run', 'name');

/**
 * Generates a filter that excludes pinned runs. This is important because the
 * main run selector query should not return runs that are already pinned or set
 * as the baseline, because those are queried separately.
 *
 * @param pinnedRunIds
 * @return Filter that excludes pinned and baseline runs, or null if there
 *         are no pinned or baseline runs
 */
export function excludedRunIdsFilter(
  pinnedRunIds: string[]
): Filter<any> | null {
  if (pinnedRunIds.length === 0) {
    return null;
  }

  return Filters.NotIn(RUN_NAME, runIdsToNames(pinnedRunIds));
}

/**
 * Generates the filter that selects any pinned or baseline or pinned ids
 * passed to the function, returning an empty Or clause if it's given no ids.
 *
 * @param pinnedRunIds
 * @return Filter that selects pinned and baseline runs, returning an empty
 *         Or clause if no ids are passed.
 */
export function pinnedRunIdsFilter(pinnedRunIds: string[]): Filter<any> {
  if (pinnedRunIds.length === 0) {
    return Filters.Or([]);
  }

  return Filters.Or(
    runIdsToNames(pinnedRunIds).map(name => Filters.Eq(RUN_NAME, name))
  );
}

/**
 * Whether a pinned run belongs to `project`.
 *
 * Pins are matched by name (see `pinnedRunIdsFilter`), and run names are only
 * unique within a project. A cross-project pin therefore can't be fetched by
 * that project's query at all, and worse, could match a same-named run that
 * does live there. Cross-project pins are a line-plot feature, and line plots
 * fetch them by ID instead, so nothing is lost by skipping them here.
 *
 * Entity and project names are case-insensitive at the data layer, so both
 * sides are lowercased. IDs that don't decode are kept — `runIdsToNames` drops
 * them downstream anyway.
 */
function isPinInProject(
  id: string,
  project: {entityName: string; projectName: string}
): boolean {
  const pinProject = runIdToProject(id);
  if (pinProject == null) {
    return true;
  }
  return (
    pinProject.entityName.toLowerCase() === project.entityName.toLowerCase() &&
    pinProject.projectName.toLowerCase() === project.projectName.toLowerCase()
  );
}

/**
 * Widens `filters` so pinned and baseline runs come back even when the run
 * filter excludes them, matching how the run selector and line plots fetch
 * them outside the filter.
 *
 * Callers must apply eyeball `selections` *around* the result (AND them on
 * afterwards), so hiding a pinned run still hides it.
 *
 * @param filters The run filter to widen.
 * @param pinnedRunIds Pinned and baseline run IDs, in any order.
 * @param project The project the filter will be run against.
 * @return The widened filter, or `filters` unchanged when there's nothing to
 *         add.
 */
export function filtersWithPinnedRuns(
  filters: Filter<any>,
  pinnedRunIds: string[],
  project: {entityName: string; projectName: string}
): Filter<any> {
  const localPins = pinnedRunIds.filter(id => isPinInProject(id, project));
  if (localPins.length === 0) {
    return filters;
  }
  // `Filters.simplify` collapses an empty group to `true`, and `simplifyInner`
  // drops it from its parent entirely. That's right inside an AND, but inside
  // an OR it would delete the match-everything branch and narrow the query
  // down to just the pins -- so when the filter already matches everything,
  // there's nothing to union with.
  if (Filters.simplify(filters) === true) {
    return filters;
  }
  return Filters.Or([filters, pinnedRunIdsFilter(localPins)]);
}

type PinnedAndBaselineRunIds = {
  pinnedRunIds?: string[];
  baselineRunId?: string;
};

type UsePinnedRunFiltersForRunSetsHook = {
  excludedIDsFilter: ReturnType<typeof excludedRunIdsFilter>;
  pinnedRunsFilter: ReturnType<typeof pinnedRunIdsFilter>;
};

export function usePinnedRunFiltersForRunSets(
  runSets: PinnedAndBaselineRunIds[] = []
): UsePinnedRunFiltersForRunSetsHook {
  const pinnedRuns = useMemo(
    () => runSets.flatMap(rs => rs.pinnedRunIds ?? []),
    [runSets]
  );
  const baselineRuns = useMemo(
    () => runSets.map(rs => rs.baselineRunId).filter(isNotNullOrUndefined),
    [runSets]
  );
  const excludedIDsFilter = useMemo(() => {
    return excludedRunIdsFilter(baselineRuns.concat(pinnedRuns));
  }, [baselineRuns, pinnedRuns]);

  const pinnedRunsFilter = useMemo(() => {
    return pinnedRunIdsFilter(baselineRuns.concat(pinnedRuns));
  }, [baselineRuns, pinnedRuns]);

  return {excludedIDsFilter, pinnedRunsFilter};
}

export function usePinnedRunFilters(
  pinnedRunIds: string[],
  baselineRunId?: string
): UsePinnedRunFiltersForRunSetsHook {
  return usePinnedRunFiltersForRunSets(
    useMemo(
      () => [{pinnedRunIds, baselineRunId}],
      [pinnedRunIds, baselineRunId]
    )
  );
}
